import { Divider, Grid, IconButton, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { useFormik, useFormikContext } from 'formik';
import { RegistrationBewareFormVM } from 'mutation/view-models/registration-beware';
import { DateTime } from 'luxon';
import * as Yup from 'yup';
// import PmiTextField from 'shell/components/shared/text-field';
import AddIcon from '@mui/icons-material/Add';
import AnomaliesList from '../anomalies-list';
import AnomaliesTable from '../../../../../../../shell/components/shared/anomalies-table';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import PmiTextField from 'shell/components/shared/text-field';
import { AnomalyResponse } from 'shell/models/anomaly-response';
import { Anomaly } from 'shell/view-models/anomaly';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { useBus, useListener } from 'shell/hooks/use-bus';
import { Cancel, Save } from '@mui/icons-material';
import { SubjectAttributes } from 'shell/models/person/subject-attributes';

const validationSchema = Yup.object().shape({
  code: Yup.string().required('Campo obbligatorio'),
  description: Yup.string(),
});

interface AnomaliesFormProps {
  disabled?: boolean;
}

const AnomaliesFormComponent = ({ disabled }: AnomaliesFormProps): React.ReactElement => {
  const formContext = useFormikContext<RegistrationBewareFormVM>();
  const { subjectAttributes } = formContext.values;
  const { userProfile } = useUserProfile();

  const [isEditMode, setIsEditMode] = useState<{ anomaly?: Anomaly; edit?: boolean } | null>(null);

  const [openModalError, setOpenModalError] = useState<{
    open?: boolean;
    message?: string;
    anomaly?: Anomaly | null;
  }>({
    open: false,
    message: '',
  });

  const [anomaliesState, setAnomaliesState] = useState<Anomaly[]>([]);

  const addAnomaly = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/gestione-anomalie/registrazione`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const deleteAnomaly = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/gestione-anomalie/cancellazione`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const editAnomaly = useHttpClient(`${__PERSON_NEW_API_URL__}/persona/note-anomalie/modifica`, {
    options: {
      method: 'PUT',
    },
  });

  const handleAddAnomaly = (values: Anomaly) => {
    (
      addAnomaly({
        data: {
          personaRettifica: {
            testataRichiesta: {
              dataDecorrenza:
                formContext.values.changeControlDataForm.effectiveDate?.toFormat('yyyy-MM-dd'),
            },
            generalitaPerRicerca: formContext.values.person?.generality,
            datiControllo: {
              tipoMutazione: ['01'],
              gestioneCF: '3',
            },
          },
          attributiSoggetto: {
            senzaFissaDimora: subjectAttributes?.homeless ? 'S' : 'N',
            soggettoCertificabile: formContext.values.subjectAttributes?.certificableSubject,
          },
          tipoAnomalia: {
            flagAnomalia: values.flag,
            descrizione: values.description,
            key: {
              ente: userProfile.datiLogin?.key?.codiceEnte || '',
              codice: values.code,
            },
          },
        },
      }) as ResponsePromise<HttpResponse<{ attributiSoggetto: SubjectAttributes }>>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');
      formContext.setFieldValue('subjectAttributes', {
        ...subjectAttributes,
        certificableSubject: response?.data.payload?.attributiSoggetto?.soggettoCertificabile,
      });
      getAnomalies({
        params: {
          cancellati: true,
          codiceNonCertificabilita:
            response?.data.payload?.attributiSoggetto?.soggettoCertificabile,
        },
        url: `${__PERSON_NEW_API_URL__}/persona/note-anomalie/anagrafiche/${formContext?.values?.person?.codicePersonaSipo}`,
      });
      formAnomaly.resetForm();
    });
  };

  const onSubmit = (values: Anomaly) => {
    if (isEditMode?.edit) {
      handleEdit(values);
    } else {
      handleAddAnomaly(values);
    }
  };

  const formAnomaly = useFormik<Anomaly>({
    initialValues: {
      code: '',
      description: '',
    },
    validationSchema,
    onSubmit,
  });

  const [anomalies, getAnomalies] = useHttpFetch<HttpResponse<AnomalyResponse>, Array<Anomaly>>(
    '',
    {
      onBootstrap: false,
      initialData: [],
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) => {
        if (response.data.payload?.messaggioErrore) {
          toast.error(response.data.payload.messaggioErrore);
        }
        return (response.data.payload?.anomalie || []).map((anomalia) => ({
          key: anomalia.key,
          code: anomalia.codiceAnomalia,
          description: anomalia.descrizioneNota,
          flag: anomalia.tipoAnomalia?.flagAnomalia,
          insertOperator: anomalia.nomeOperatoreInserimento,
          cancelOperator: anomalia.nomeOperatoreCancellazione,
          insertDate: anomalia.key?.dataAnnotazione
            ? DateTime.fromISO(anomalia.key?.dataAnnotazione)
            : undefined,
          cancelDate: anomalia.dataCancellazione
            ? DateTime.fromISO(anomalia.dataCancellazione)
            : undefined,
        }));
      },
    }
  );

  useEffect(() => {
    if (formContext.values.person?.codicePersonaSipo) {
      getAnomalies({
        params: {
          cancellati: true,
          codiceNonCertificabilita: formContext.values.subjectAttributes?.certificableSubject,
        },
        url: `${__PERSON_NEW_API_URL__}/persona/note-anomalie/anagrafiche/${formContext.values.person.codicePersonaSipo}`,
      });
    }
  }, [
    formContext?.values?.person?.codicePersonaSipo,
    formContext?.values?.subjectAttributes?.certificableSubject,
  ]);

  useListener('RESET_ANOMALIES', () => {
    setAnomaliesState([]);
    formAnomaly.resetForm();
  });

  useEffect(() => {
    setAnomaliesState(anomalies.data as Anomaly[]);
  }, [anomalies.data]);

  useEffect(() => {
    if (anomaliesState.length > 0) {
      bus.emit('ACCORDION_OPEN', true);
    }
  }, [anomaliesState]);

  const handleDelete = (anomaly: Anomaly) => {
    return (
      deleteAnomaly({
        data: {
          personaRettifica: {
            testataRichiesta: {
              dataDecorrenza:
                formContext.values.changeControlDataForm?.effectiveDate?.toFormat('yyyy-MM-dd'),
            },
            generalitaPerRicerca: formContext.values.person?.generality,
            datiControllo: {
              tipoMutazione: ['01'],
              gestioneCF: '3',
            },
          },
          attributiSoggetto: {
            senzaFissaDimora: subjectAttributes?.homeless ? 'S' : 'N',
            soggettoCertificabile: formContext.values.subjectAttributes?.certificableSubject,
          },
          notaAnomalia: {
            key: anomaly.key,
          },
        },
      }) as ResponsePromise<HttpResponse<{ attributiSoggetto: SubjectAttributes }>>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');
      formContext.setFieldValue('subjectAttributes', {
        ...subjectAttributes,
        certificableSubject: response?.data.payload?.attributiSoggetto?.soggettoCertificabile,
      });
      getAnomalies({
        url: `${__PERSON_NEW_API_URL__}/persona/note-anomalie/anagrafiche/${formContext?.values?.person?.codicePersonaSipo}`,
        params: {
          cancellati: true,
          codiceNonCertificabilita: response?.data.payload.attributiSoggetto?.soggettoCertificabile,
        },
      });
    });
  };

  const handleEdit = (anomaly: Anomaly) => {
    (
      editAnomaly({
        data: {
          personaRettifica: {
            testataRichiesta: {
              dataDecorrenza:
                formContext.values.changeControlDataForm?.effectiveDate?.toFormat('yyyy-MM-dd'),
            },
            generalitaPerRicerca: formContext.values.person?.generality,
            datiControllo: {
              tipoMutazione: ['01'],
              gestioneCF: '3',
            },
          },
          attributiSoggetto: {
            senzaFissaDimora: subjectAttributes?.homeless ? 'S' : 'N',
            soggettoCertificabile: formContext.values.subjectAttributes?.certificableSubject,
          },
          notaAnomalia: {
            key: anomaly.key,
            descrizioneNota: anomaly.description,
          },
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      getAnomalies({
        url: `${__PERSON_NEW_API_URL__}/persona/note-anomalie/anagrafiche/${formContext?.values?.person?.codicePersonaSipo}`,
        params: {
          cancellati: true,
          codiceNonCertificabilita: formContext.values.subjectAttributes?.certificableSubject,
        },
      });
      formAnomaly.resetForm();
      setIsEditMode(null);
    });
  };

  const disableForm =
    userProfile.datiLogin?.codiceIstat !==
      formContext.values.person?.generality?.idSchedaSoggettoComune?.idSchedaSoggettoComuneIstat ||
    disabled;

  const onDelete = (anomaly: Anomaly) => {
    setOpenModalError({
      open: true,
      anomaly,
      message: `Sei sicuro di voler cancellare l'anomalia ${anomaly.code} - ${anomaly.description}?`,
    });
  };

  const bus = useBus();

  return (
    <>
      <Grid item xs={12}>
        <PmiTitleDivider>Note e Anomalie</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={5}>
          <AnomaliesList
            label="Codice anomalia"
            value={formAnomaly.values.code as string}
            onChange={(anomaly) => {
              if (anomaly) {
                formAnomaly.setValues(anomaly);
              }
            }}
            required
            error={formAnomaly.touched?.code && Boolean(formAnomaly.errors?.code)}
            disabled={disableForm || !!isEditMode?.edit}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          {isEditMode ? (
            <>
              <IconButton
                color="cdmBrandSecondary"
                aria-label="add anomaly"
                component="span"
                onClick={() => formAnomaly.handleSubmit()}
                disabled={disableForm}
              >
                <Save />
              </IconButton>
              <IconButton
                color="cdmBrandSecondary"
                aria-label="add anomaly"
                component="span"
                onClick={() => {
                  setIsEditMode(null);
                  formAnomaly.resetForm();
                }}
                disabled={disableForm || !formAnomaly.isValid}
              >
                <Cancel />
              </IconButton>
            </>
          ) : (
            <IconButton
              color="cdmBrandSecondary"
              aria-label="add anomaly"
              component="span"
              onClick={() => formAnomaly.handleSubmit()}
              disabled={disableForm}
            >
              <AddIcon />
            </IconButton>
          )}
          <Typography color="secondary" display="inline-block">
            {isEditMode ? 'Modifica' : 'Aggiungi nuova'} nota o anomalia
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Nota"
          aria-label="Nota"
          fullWidth
          onChange={(event) => {
            formAnomaly.setFieldValue('description', event?.target.value);
          }}
          multiline
          rows={5}
          id="description"
          value={formAnomaly.values?.description || ''}
          type="text"
          error={formAnomaly.touched?.description && Boolean(formAnomaly.errors?.description)}
          helperText={formAnomaly.errors?.description}
          disabled={disableForm}
        />
      </Grid>
      <DeleteAlertModal
        title="Conferma cancellazione"
        message={openModalError.message || ''}
        open={openModalError.open || false}
        onClose={() => {
          setOpenModalError({
            open: false,
            message: '',
            anomaly: null,
          });
        }}
        onNo={() => {
          setOpenModalError({
            open: false,
            message: '',
            anomaly: null,
          });
        }}
        onYes={() => {
          handleDelete(openModalError.anomaly as Anomaly).then(() => {
            setOpenModalError({
              open: false,
              message: '',
            });
          });
        }}
      />
      <Grid item xs={12}>
        <AnomaliesTable
          anomalies={anomaliesState as Anomaly[]}
          disabled={disableForm || false}
          onDelete={onDelete}
          onEdit={(anomaly) => {
            setIsEditMode({ edit: true, anomaly });
            formAnomaly.setValues({
              ...anomaly,
              code: anomaly.code ? (Number.parseInt(anomaly.code as string, 10) as number) : '',
            });
          }}
        />
      </Grid>
    </>
  );
};

AnomaliesFormComponent.displayName = 'Anomalies form component';

const AnomaliesForm = memo(AnomaliesFormComponent);

export default AnomaliesForm;
